import { v4 as uuid } from 'uuid';
import { Event, LinkedList } from '@jtjs/core-data';
import { ReactNode } from 'react';

export interface OverlayData {
  contents: ReactNode | ReactNode[];
  id?: string;
}

export type OnAddOverlayListener = (
  addedOverlay: OverlayData,
  overlays: LinkedList<OverlayData>
) => void;
export type OnRemoveOverlayListener = (
  removedOverlay: OverlayData,
  overlays: LinkedList<OverlayData>
) => void;

export class OverlayService {
  static onAddOverlay = new Event<OnAddOverlayListener>();
  static onRemoveOverlay = new Event<OnRemoveOverlayListener>();

  private static _overlays: LinkedList<OverlayData> = new LinkedList();

  /**
   * The currently active overlay.
   */
  public static get activeOverlay() {
    return this._overlays.last;
  }

  /**
   * Adds an overlay and triggers the `onAddOverlay` event.
   *
   * @param overlay - The overlay to add.
   * @returns - The ID of the added overlay. This should be kept so that it can be used when
   * removing the overlay.
   */
  static addOverlay(overlay: OverlayData): string {
    if (!overlay.id) {
      overlay.id = uuid();
    }

    this._overlays.add(overlay);

    this.onAddOverlay.trigger(overlay, this._overlays);

    return overlay.id;
  }

  /**
   * Removes an overlay specified by its ID. If the provided ID doesn't match any registered overlay,
   * nothing will happen. If an overlay is succesfully removed, the `onRemovedOverlay` event will be triggered.
   *
   * @param overlayId - The ID of the overlay to remove.
   * @returns - Whether an overlay was removed. If the provided ID doesn't match any of the
   * registered overlays, this will be false.
   */
  static removeOverlay(overlayId: string): boolean {
    let removedOverlay: OverlayData | null = null;

    this._overlays.removeBy((overlayData) => {
      if (overlayData.id === overlayId) {
        removedOverlay = overlayData;

        return true;
      }

      return false;
    });

    if (removedOverlay) {
      this.onRemoveOverlay.trigger(removedOverlay, this._overlays);
    }

    return !!removedOverlay;
  }
}
